import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { blueGrey } from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import { Box } from '@mui/system'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useUpdateOrderMutation } from '@/app/services/orders'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxLocalizationProvider from '@/common/components/TbxLocalizationProvider/TbxLocalizationProvider'
import { ORDER_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

const classes = {
    disabledInput: {
        '& .Mui-disabled': {
            textFillColor: '#34495E',
        },
        '& .MuiInput-input.Mui-disabled': {
            fontWeight: 'bold',
            color: 'inherit',
            textFillColor: '#34495E',
        },
    },
    divider: {
        borderStyle: 'dashed',
        borderColor: blueGrey[200],
    },
}

const OrderDetailsOptions = ({ order }) => {
    const { t } = useTranslation()

    const { enqueueSnackbar } = useSnackbar()

    const organisationId = useSelector(selectOrganisationId)

    const [updateOrder] = useUpdateOrderMutation()

    const [orderNumber, setOrderNumber] = useState(order?.name ?? '')
    const [orderRFQ, setOrderRFQ] = useState(order?.rfqNumber ?? '')
    const [purchaseOrder, setPurchaseOrder] = useState(order?.poNumber ?? null)

    const userTimeZone = dayjs.tz.guess()

    const isEditable = [ORDER_STATUS_ARRAY.NotCalculated, ORDER_STATUS_ARRAY.Calculated].includes(order?.status)

    const isDraft = [ORDER_STATUS_ARRAY.Draft].includes(order?.status)

    const isIssued = [ORDER_STATUS_ARRAY.Issued].includes(order?.status)

    const isQuoted = [ORDER_STATUS_ARRAY.Quoted].includes(order?.status)

    const isOrdered = [ORDER_STATUS_ARRAY.Ordered].includes(order?.status)

    const isInvoiced = [ORDER_STATUS_ARRAY.Invoiced].includes(order?.status)

    const isDisabled = [
        ORDER_STATUS_ARRAY.Lost,
        ORDER_STATUS_ARRAY.Cancelled,
        ORDER_STATUS_ARRAY.Rejected,
        ORDER_STATUS_ARRAY.Voided,
    ].includes(order?.status)

    const updateOrderHandler = async (attribute, value) => {
        if (value === '' && !['poNumber'].includes(attribute)) return

        try {
            await updateOrder({
                organisationId,
                orderId: order?.id,
                order: {
                    ...order,
                    [attribute]: value,
                },
            }).unwrap()
        } catch (error) {
            enqueueSnackbar(t('Failed to update the order'), {
                variant: 'error',
            })
        }
    }

    const debouncedUpdateOrderHandler = useDebouncedCallback(updateOrderHandler, 1000)

    const handleOrderNumberChange = (event) => {
        setOrderNumber(event.target.value)
        debouncedUpdateOrderHandler('name', event.target.value)
    }

    const handleOrderNumberBlur = (event) => {
        if (event.target.value === '') {
            setOrderNumber(order?.name ?? '')
        }
    }

    const handleRFQChange = (event) => {
        setOrderRFQ(event.target.value)
        debouncedUpdateOrderHandler('rfqNumber', event.target.value)
    }

    const handleRFQBlur = (event) => {
        if (event.target.value === '') {
            setOrderRFQ(order?.rfqNumber ?? '')
        }
    }

    const handlePurchaseOrderChange = (event) => {
        setPurchaseOrder(event.target.value)
        debouncedUpdateOrderHandler('poNumber', event.target.value)
    }

    return (
        <TbxLocalizationProvider>
            <Box
                key={`${order?.id}-details-options`}
                sx={{
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    display: 'flex',
                    gap: 3,
                    justifyContent: 'space-between',
                }}
            >
                <Box
                    sx={{
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        justifyContent: 'space-between',
                        width: 140,
                    }}
                >
                    <TextField
                        color="secondary"
                        label={t('Order number')}
                        size="small"
                        slotProps={{
                            input: {
                                disableUnderline: true,
                            },

                            htmlInput: {
                                'data-testid': 'order-number-input',
                            },

                            inputLabel: {
                                shrink: true,
                            },
                        }}
                        sx={classes.disabledInput}
                        value={orderNumber}
                        variant="standard"
                        disabled
                        onBlur={handleOrderNumberBlur}
                        onChange={handleOrderNumberChange}
                    />
                    {isDraft ? (
                        <DatePicker
                            format="DD-MMM-YYYY"
                            label={t('Order saved date')}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    size: 'small',
                                    sx: classes.disabledInput,
                                    color: 'secondary',
                                    inputProps: {
                                        'data-testid': 'order-issue-date-input',
                                    },
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                    InputLabelProps: {
                                        shrink: true,
                                    },
                                },
                            }}
                            timezone={userTimeZone}
                            value={dayjs(order?.lastModifiedDateUtc)}
                            disabled
                            disableOpenPicker
                        />
                    ) : null}
                    {isIssued || isQuoted || isOrdered || isInvoiced || isDisabled ? (
                        <DatePicker
                            format="DD-MMM-YYYY"
                            label={t('Order issue date')}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    size: 'small',
                                    sx: classes.disabledInput,
                                    color: 'secondary',
                                    inputProps: {
                                        'data-testid': 'order-issue-date-input',
                                    },
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                    InputLabelProps: {
                                        shrink: true,
                                    },
                                },
                            }}
                            timezone={userTimeZone}
                            value={dayjs(order?.issuedDateUtc)}
                            disabled
                            disableOpenPicker
                        />
                    ) : null}
                </Box>
                <Box
                    sx={{
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        justifyContent: 'space-between',
                        width: 140,
                    }}
                >
                    <TextField
                        color="secondary"
                        disabled={!isEditable ? !isDraft : null}
                        label={t('RFQ number')}
                        size="small"
                        slotProps={{
                            input: {
                                disableUnderline: !isEditable ? !isDraft : null,
                            },

                            htmlInput: {
                                'data-testid': 'order-rfq-input',
                            },

                            inputLabel: {
                                shrink: true,
                            },
                        }}
                        sx={classes.disabledInput}
                        value={orderRFQ}
                        variant="standard"
                        onBlur={handleRFQBlur}
                        onChange={handleRFQChange}
                    />
                    {(isEditable || isDraft || isIssued || isQuoted) && order?.expiryDate ? (
                        <DatePicker
                            format="DD-MMM-YYYY"
                            label={t('Prices valid until')}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    size: 'small',
                                    sx: classes.disabledInput,
                                    color: 'secondary',
                                    placeholder: t('Select a date'),
                                    inputProps: {
                                        'data-testid': 'order-expiry-date-input',
                                    },
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                    InputLabelProps: {
                                        shrink: true,
                                    },
                                },
                                field: {
                                    readOnly: true,
                                },
                            }}
                            timezone={userTimeZone}
                            value={dayjs(order?.expiryDate)}
                            disabled
                            disableOpenPicker
                        />
                    ) : null}
                </Box>
            </Box>
            {isDraft || isIssued || isQuoted || isOrdered || isInvoiced || isDisabled ? (
                <Divider
                    orientation="vertical"
                    sx={classes.divider}
                    flexItem
                />
            ) : null}
            {isDraft || isIssued || isQuoted || isOrdered || isInvoiced || isDisabled ? (
                <Box
                    sx={{
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        display: 'flex',
                        gap: 3,
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            alignItems: 'flex-start',
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            justifyContent: 'space-between',
                            width: 140,
                        }}
                    >
                        <TextField
                            color="secondary"
                            disabled={isDisabled}
                            label={t('Purchase order number')}
                            size="small"
                            slotProps={{
                                input: {
                                    disableUnderline: isDisabled,
                                },

                                htmlInput: {
                                    'data-testid': 'order-purchase-order-number-input',
                                },

                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            sx={classes.disabledInput}
                            value={purchaseOrder}
                            variant="standard"
                            onChange={handlePurchaseOrderChange}
                        />
                    </Box>
                </Box>
            ) : null}
        </TbxLocalizationProvider>
    )
}

OrderDetailsOptions.propTypes = {
    order: PropTypes.object,
}

export default OrderDetailsOptions
